/* CarWrapVinyl.com — base behaviour (Builder A)
   Mobile nav drawer, sticky header shrink, desktop dropdowns,
   reduced-motion flag, smooth in-page anchor scroll.
   Exposes helpers on the single namespace window.CWV. */
(function () {
  'use strict';

  var CWV = (window.CWV = window.CWV || {});
  var doc = document;
  var root = doc.documentElement;
  var motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;

  function listen(mq, fn) {
    if (!mq) return;
    if (mq.addEventListener) mq.addEventListener('change', fn);
    else if (mq.addListener) mq.addListener(fn);
  }

  /* ---------- Reduced motion ---------- */
  CWV.reducedMotion = function () {
    return !!(motionQuery && motionQuery.matches);
  };
  function syncMotion() {
    root.setAttribute('data-motion', CWV.reducedMotion() ? 'reduce' : 'full');
  }
  syncMotion();
  listen(motionQuery, syncMotion);

  /* ---------- Script loader (shared) ---------- */
  var loaded = {};
  CWV.loadScript = function (src, attrs) {
    if (loaded[src]) return loaded[src];
    loaded[src] = new Promise(function (resolve, reject) {
      var s = doc.createElement('script');
      s.src = src;
      s.async = true;
      if (attrs) Object.keys(attrs).forEach(function (k) { s.setAttribute(k, attrs[k]); });
      s.onload = function () { resolve(s); };
      s.onerror = function () { delete loaded[src]; reject(new Error('Failed to load ' + src)); };
      doc.head.appendChild(s);
    });
    return loaded[src];
  };

  /* ---------- Focus trap & scroll lock (shared) ---------- */
  var FOCUSABLE = 'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),iframe,audio[controls],video[controls],summary,[tabindex]:not([tabindex="-1"]),[contenteditable="true"]';

  CWV.focusable = function (container) {
    return Array.prototype.filter.call(container.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
    });
  };

  CWV.trapFocus = function (container, initial) {
    function onKeydown(e) {
      if (e.key !== 'Tab') return;
      var items = CWV.focusable(container);
      if (!items.length) { e.preventDefault(); container.focus(); return; }
      var first = items[0];
      var last = items[items.length - 1];
      var active = doc.activeElement;
      if (e.shiftKey && (active === first || !container.contains(active))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !container.contains(active))) {
        e.preventDefault();
        first.focus();
      }
    }
    doc.addEventListener('keydown', onKeydown, true);
    var target = initial || CWV.focusable(container)[0] || container;
    if (target === container && !container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
    try { target.focus({ preventScroll: true }); } catch (err) { target.focus(); }
    return function release() {
      doc.removeEventListener('keydown', onKeydown, true);
    };
  };

  var lockCount = 0;
  CWV.lockScroll = function (lock) {
    if (lock) {
      lockCount += 1;
      if (lockCount === 1) root.classList.add('cwv-scroll-lock');
    } else {
      lockCount = Math.max(0, lockCount - 1);
      if (lockCount === 0) root.classList.remove('cwv-scroll-lock');
    }
  };

  /* ---------- Header: shrink on scroll, drawer, dropdowns ---------- */
  function initHeader(scope) {
    var header = (scope || doc).querySelector('[data-cwv-header]');
    if (!header || header.cwvReady) return;
    header.cwvReady = true;

    var ticking = false;
    function update() {
      header.classList.toggle('is-scrolled', (window.scrollY || window.pageYOffset) > 24);
      ticking = false;
    }
    function onScroll() {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    update();

    function setHeaderHeight() {
      var h = Math.round(header.getBoundingClientRect().height);
      if (h > 0) root.style.setProperty('--header-h', h + 'px');
    }
    setHeaderHeight();
    window.addEventListener('resize', setHeaderHeight, { passive: true });

    /* Desktop dropdowns: close on outside click / Escape */
    var dropdowns = header.querySelectorAll('details.cwv-dd');
    if (dropdowns.length) {
      doc.addEventListener('click', function (e) {
        Array.prototype.forEach.call(dropdowns, function (dd) {
          if (dd.open && !dd.contains(e.target)) dd.open = false;
        });
      });
      header.addEventListener('keydown', function (e) {
        if (e.key !== 'Escape') return;
        Array.prototype.forEach.call(dropdowns, function (dd) {
          if (dd.open) {
            dd.open = false;
            var s = dd.querySelector('summary');
            if (s) s.focus();
          }
        });
      });
    }

    /* Drawer */
    var toggle = header.querySelector('[data-cwv-drawer-open]');
    var drawer = header.querySelector('[data-cwv-drawer]');
    if (!toggle || !drawer) return;
    var panel = drawer.querySelector('.cwv-drawer__panel') || drawer;
    var release = null;
    var hideTimer = null;
    var openSeq = 0;

    function onEsc(e) {
      if (e.key === 'Escape') close();
    }
    function open() {
      clearTimeout(hideTimer);
      drawer.hidden = false;
      toggle.setAttribute('aria-expanded', 'true');
      /* fade in on the next frames; the timer makes sure the drawer still ends fully opaque
         when animation frames are paused (background tab) */
      var seq = ++openSeq;
      var show = function () { if (seq === openSeq && !drawer.hidden) drawer.classList.add('is-open'); };
      window.requestAnimationFrame(function () { window.requestAnimationFrame(show); });
      setTimeout(show, 120);
      CWV.lockScroll(true);
      release = CWV.trapFocus(panel, drawer.querySelector('[data-cwv-drawer-close]'));
      doc.addEventListener('keydown', onEsc);
    }
    function close(returnFocus) {
      if (drawer.hidden) return;
      openSeq += 1;
      drawer.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      if (release) { release(); release = null; }
      CWV.lockScroll(false);
      doc.removeEventListener('keydown', onEsc);
      clearTimeout(hideTimer);
      if (CWV.reducedMotion()) drawer.hidden = true;
      else hideTimer = setTimeout(function () { drawer.hidden = true; }, 300);
      if (returnFocus !== false) toggle.focus();
    }

    toggle.addEventListener('click', function () {
      if (drawer.hidden) open(); else close();
    });
    Array.prototype.forEach.call(drawer.querySelectorAll('[data-cwv-drawer-close]'), function (btn) {
      btn.addEventListener('click', function () { close(); });
    });
    drawer.addEventListener('click', function (e) {
      if (e.target === drawer) { close(); return; }
      var link = e.target.closest ? e.target.closest('a[href]') : null;
      if (link) close(false);
    });
    var desktop = window.matchMedia ? window.matchMedia('(min-width: 1101px)') : null;
    listen(desktop, function (mq) { if (mq.matches) close(false); });
  }

  /* ---------- Smooth in-page anchor scroll ---------- */
  doc.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    var link = e.target.closest ? e.target.closest('a[href*="#"]') : null;
    if (!link || link.hasAttribute('data-no-smooth') || link.target === '_blank') return;
    var url;
    try { url = new URL(link.href, window.location.href); } catch (err) { return; }
    if (url.origin !== window.location.origin || url.pathname !== window.location.pathname || url.search !== window.location.search) return;
    if (!url.hash || url.hash === '#') return;
    var id;
    try { id = decodeURIComponent(url.hash.slice(1)); } catch (err2) { id = url.hash.slice(1); }
    var target = doc.getElementById(id);
    if (!target) return;
    e.preventDefault();
    target.scrollIntoView({ behavior: CWV.reducedMotion() ? 'auto' : 'smooth', block: 'start' });
    if (window.history && window.history.pushState) window.history.pushState(null, '', url.hash);
    if (!target.matches('a[href],button,input,select,textarea,summary,[tabindex]')) target.setAttribute('tabindex', '-1');
    try { target.focus({ preventScroll: true }); } catch (err3) { /* noop */ }
  });

  /* ---------- Boot ---------- */
  function boot() { initHeader(doc); }
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Theme editor support */
  doc.addEventListener('shopify:section:load', function (e) { initHeader(e.target); });

  CWV.initHeader = initHeader;
})();
